import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

// Types
import {
  ExternalWorksBySegment,
  LinkedWorkRef,
  RouteSegment,
} from '../../../../../types/surveillanceRoute';

// Utils — reutiliza el mapeo canónico sentido→key i18n y el formateador de
// PK (mismos helpers que usan TimerOverlay/RouteSegmentOT/PDF) en vez de
// duplicar lógica local, como hacía el acordeón viejo.
import {
  directionNameKey,
  formatPK,
  formatSegmentDuration,
  formatSegmentKm,
  canSegmentTakeLinks,
  isSegmentCauseChangeable,
  isSegmentPendingSync,
  isSegmentReviewable,
  computeReviewProgress,
  sortSegmentsByStartDate,
  stripLeadingWord,
} from '../../../../../utils/surveillanceRoute';

// Styles
import {
  SegmentsContainer,
  TableScroll,
  Table,
  Thead,
  Tbody,
  Th,
  Td,
  SegmentRow,
  PendingMark,
  SplitMark,
  MarkTip,
  CellActions,
  SegmentNumber,
  NumberTd,
  TreeGuide,
  StopLabel,
  StopNote,
  DirectionCell,
  DirectionIcon,
  CauseBadge,
  CauseIcon,
  CauseLabel,
  ActiveBadge,
  EmptyCellText,
  LinkText,
  PendingCellText,
  DescriptionCellText,
  CellStack,
  LinkWithState,
  PreviewBadge,
  ReviewHeader,
  ReviewTh,
  ReviewCheckBox,
  VisuallyHidden,
} from './styles';
import { AvisoOrderCell } from './AvisoOrderCell';
import { LinkedWorkTags } from '../../../../../components/display/LinkedWorkTags';
import ReviewCell, { segmentNumberCellId } from './ReviewCell';
import { getReviewDisplay } from '../../../../../utils/segmentReviewOverlay';
import type { AutoReview } from '../../../../../utils/segmentAutoReview';
import { orderStateVariant } from '../../../../../components/display/SpecialTag/orderStateVariant';
import { SpecialTag } from '../../../../../components/display/SpecialTag';
import { roadIdsInvolved } from '../../../../../utils/roadStretches';
import { getCachedRoadMeshes } from '../../../../../hooks/usePrecacheRouteMesh';
import {
  isLiveLinkedBasicAction,
  isSegmentPendingAction,
  liveLinkedBasicActionOrder,
} from '../../../../../utils/segmentLinkedBasicAction';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface SegmentListProps {
  segments: RouteSegment[];
  onSegmentClick?: (segment: RouteSegment) => void;
  selectedSegmentId?: string;
  /**
   * «Mover tramos» (plan 2026-10-15 § 3): varias filas marcadas a la vez —el
   * rango que se mueve—, con el mismo resaltado que la fila seleccionada.
   */
  selectedSegmentIds?: string[];
  /**
   * Corrección de gestión (spec 2026-07-31): con `true` aparece una columna
   * final con el botón "Corregir" en cada fila. Lo decide la página, que
   * conoce el rol y el estado del recorrido — la tabla no hace RBAC.
   */
  canEditSegments?: boolean;
  onEditSegment?: (segment: RouteSegment) => void;
  /**
   * «Registrar parada» (spec 2026-10-06): con `true`, la celda de «Corregir»
   * ofrece también «Parada» y, si el servidor lo aceptaría, «Deshacer». Solo
   * gestión: el modo operario de «Corregir» no lo tiene.
   */
  canRegisterStops?: boolean;
  onRegisterStop?: (segment: RouteSegment) => void;
  onUndoStop?: (segment: RouteSegment) => void;
  /**
   * «Transferir tramo» (spec 2026-10-07): con `true`, la celda ofrece también
   * «Transferir» en los tramos cerrados, no anulados y sin partir. Solo
   * gestión y con el recorrido pendiente de validar (lo decide la página).
   */
  canTransferSegments?: boolean;
  onTransferSegment?: (segment: RouteSegment) => void;
  /**
   * Cambiar la causa (2026-10-07): con `true` y `onChangeCause` aparece la
   * columna «Cambiar causa», con un botón en cada tramo cerrado que abre el
   * mismo modal que la etiqueta «Causa: X» de «Órdenes asociadas». Lo decide
   * la página (rol); la tabla solo decide por fila si el tramo admite el
   * cambio.
   */
  canChangeCause?: boolean;
  onChangeCause?: (segment: RouteSegment) => void;
  /**
   * Columna «Vincular» (2026-10-08): lo que la card del tramo de «Órdenes
   * asociadas» deja hacer con sus vínculos. La página decide los permisos
   * (los de la card); la tabla, qué toca en cada fila según causa y vínculo.
   *  - `canLinkAviso`: vincular, editar o desvincular la OT del aviso.
   *  - `canLinkAction`: «Vincular a actuación» en un tramo Actuación sin básica.
   * Cada permiso cuenta solo si llegan sus callbacks: sin ellos (vista previa
   * de «Registrar parada») no hay columna.
   */
  canLinkAviso?: boolean;
  canLinkAction?: boolean;
  onLinkAviso?: (segment: RouteSegment) => void;
  onChangeAviso?: (segment: RouteSegment) => void;
  onUnlinkAviso?: (segment: RouteSegment) => void;
  onLinkAction?: (segment: RouteSegment) => void;
  /**
   * Tramo cuyo «Desvincular» está viajando (revisión M2): su aviso se pinta
   * sin acciones hasta que vuelve, para que un segundo toque no haga nada.
   */
  busyAvisoSegmentId?: string | null;
  /**
   * Trabajos que atendieron el aviso de cada tramo (spec 2026-10-27): salen
   * como pills «Resuelto por X» bajo la OT del aviso. Solo la página los pasa.
   */
  externalWorksBySegmentId?: ExternalWorksBySegment;
  /** Abrir un trabajo del aviso. Llega por prop: la tabla no usa el router. */
  onOpenLinkedWork?: (work: LinkedWorkRef, segment: RouteSegment) => void;
  /**
   * Nombres de las carreteras del recorrido, por id (multi-carretera
   * 2026-08-15). Con MÁS DE UNA aparece la columna «Carretera»: sin ella el PK
   * de la tabla es ambiguo, porque una troncal y su ramal comparten
   * kilometraje. Con una sola no se pinta — sería una columna con el mismo
   * valor en todas las filas.
   */
  roadNamesById?: Record<string, string>;
  /**
   * Abrir la Actuació bàsica y la OT de sala vinculadas. Los pone la página
   * (14-oct): la tabla ya no usa el router, para poder pintarla también dentro
   * de un modal (los modales viven FUERA del árbol del router — mismo cambio
   * que `InputRadioTagTable` el 2026-07-09). Sin ellos, los ROID se pintan
   * como texto.
   */
  onOpenBasicAction?: (actionId: string) => void;
  onOpenExternalOrder?: (orderId: string, type?: string | null) => void;
  /**
   * Nº de la primera fila (por defecto 1). La vista previa de «Registrar
   * parada» enseña solo las piezas, con el número que tendrán en la tabla.
   */
  firstNumber?: number;
  /**
   * «Así quedará» de «Registrar parada» (29-oct): ids de las piezas cuya
   * parada aún no tiene causa; su celda «Causa» lo dice en ámbar.
   */
  missingCauseIds?: readonly string[];
  /**
   * «Así quedará» de «Registrar parada» (29-oct), por id de tramo. Ver
   * `SegmentRowNote`.
   */
  rowNotes?: Record<string, SegmentRowNote>;
  /**
   * Las filas ya vienen en su orden (el árbol de «Así quedará», 30-oct): no se
   * reordenan por hora, que pondría al padre detrás de sus hijos.
   */
  keepOrder?: boolean;
  /**
   * «Tramos revisados» (spec 2026-10-27): con `true` y `onToggleReview` aparece
   * una columna PRIMERA con la casilla de revisado. Lo decide la página (rol y
   * estado del recorrido). `reviewOverlay` son las marcas aún sin confirmar
   * (capa local + cola offline) y manda sobre lo que trae el tramo.
   */
  canReview?: boolean;
  reviewOverlay?: ReadonlyMap<string, boolean>;
  /** Tramos revisados solos por el estado de su actuación (casilla marcada y bloqueada). */
  autoReviews?: ReadonlyMap<string, AutoReview>;
  onToggleReview?: (segmentIds: string[], reviewed: boolean) => void;
  /** Con todos marcados, la casilla de cabecera pide desmarcarlos (la página confirma). */
  onRequestUnreviewAll?: () => void;
  /**
   * Petición de llevar una fila a la vista («Siguiente sin revisar»). `seq`
   * cambia en cada petición: así se baja aunque la fila ya estuviera
   * seleccionada y el usuario se hubiera ido a otra parte de la tabla.
   */
  scrollRequest?: { segmentId: string; seq: number } | null;
}

/**
 * Cómo pintar una fila de «Así quedará» (29-oct; árbol, 30-oct): `muted` la
 * pone en gris (lo que hay hoy), `badge` es una etiqueta junto al Nº y `kind`
 * queda en `data-preview-kind`. Con `kind: 'stop'` la fila es una parada: en
 * vez del Nº dice `label`, no gasta número y su «Causa» es `causeNote` (o «-»).
 * `number` fija el Nº de la fila sin gastarlo (el padre del árbol) y `tree`
 * la sangra y le pinta las líneas que la unen con su padre.
 */
export type SegmentRowNote = {
  muted?: boolean;
  badge?: string;
  kind?: string;
  number?: number;
  label?: string;
  causeNote?: string;
  tree?: { depth: 1 | 2; last?: boolean; through?: boolean };
};

const BASE_COLUMN_COUNT = 12;

// ═══════════════════════════════════════════════════════════════════
// COMPONENT — feedback usuario 2026-07-07: de tarjetas colapsables
// agrupadas POR SENTIDO (title "Tramo {indexType}" repetido en cada
// sentido → "Tramo 1, Tramo 1, Tramo 2") a UNA tabla plana con todos los
// tramos en orden cronológico real (por `startDate`), sentido por fila,
// siguiendo la arquitectura de tabla canónica de la app (cabecera gris +
// filas alineadas — mismo stylesheet que `OperatorsSelectionTable` y las
// tablas de gestión).
// ═══════════════════════════════════════════════════════════════════

/**
 * ¿Este tramo existe solo en la cola offline? Lo marca `mergePendingSegments`
 * al fusionarlo con los del servidor.
 */

/**
 * Las líneas que unen una fila del árbol de «Así quedará» con su padre (30-oct):
 * nivel 1 «├» (o «└» la última pieza); nivel 2 «└» bajo su pieza, con la línea
 * de las piezas pasando por delante si su pieza no es la última.
 */
function TreeGuides({
  tree,
}: {
  tree: NonNullable<SegmentRowNote['tree']>;
}): JSX.Element {
  if (tree.depth === 1) {
    return <TreeGuide $level={1} $shape={tree.last ? 'elbow' : 'tee'} />;
  }
  return (
    <>
      {tree.through && <TreeGuide $level={1} $shape="pass" />}
      <TreeGuide $level={2} $shape="elbow" />
    </>
  );
}

export default function SegmentList({
  segments,
  onSegmentClick,
  selectedSegmentId,
  selectedSegmentIds,
  canEditSegments = false,
  onEditSegment,
  canRegisterStops = false,
  onRegisterStop,
  onUndoStop,
  canTransferSegments = false,
  onTransferSegment,
  canChangeCause = false,
  onChangeCause,
  canLinkAviso = false,
  canLinkAction = false,
  onLinkAviso,
  onChangeAviso,
  onUnlinkAviso,
  onLinkAction,
  busyAvisoSegmentId,
  externalWorksBySegmentId,
  onOpenLinkedWork,
  roadNamesById,
  onOpenBasicAction,
  onOpenExternalOrder,
  firstNumber = 1,
  missingCauseIds,
  rowNotes,
  keepOrder = false,
  canReview = false,
  reviewOverlay,
  autoReviews,
  onToggleReview,
  onRequestUnreviewAll,
  scrollRequest,
}: SegmentListProps): JSX.Element {
  // La columna «Carretera» solo aparece si hay algo que decir: con una sola vía
  // en todas las filas sería una columna con el mismo valor repetido, y la
  // tabla del RV ya va justa de ancho en tablet.
  //
  // 🔴 Pero «una sola vía» es de los TRAMOS, no del recorrido. Quitar una
  // carretera del recorrido NO reescribe el pasado —los tramos guardan por
  // dónde pasaron, y eso es justo lo que se les pide—, así que mirando solo el
  // recorrido la columna desaparecía y la tabla se quedaba diciendo que todo
  // fue por la vía que queda. La ficha de cada tramo seguía enseñando las dos,
  // o sea que las dos pantallas se contradecían (cazado el 2026-08-24 al
  // probar mc45).
  const roadIdsOfRoute = Object.keys(roadNamesById ?? {});
  // 🔴 La columna va SIEMPRE (2026-08-25). Antes salía solo con más de una vía,
  // y eso obligaba a preguntarse si faltaba porque el recorrido tiene una sola o
  // porque el dato no estaba. La misma información se leía de cuatro formas
  // distintas según la pantalla; ahora todas dicen la vía y su rango igual.
  const showRoadColumn = roadIdsInvolved(roadIdsOfRoute, segments).length > 0;
  // Las mallas ya descargadas, para que los km midan el asfalto y no la resta
  // de etiquetas (kilómetros largos, 2026-08-26). Si no hay malla el número
  // sale como siempre.
  const meshesDeLasVias = useMemo(
    () => getCachedRoadMeshes(roadIdsInvolved(roadIdsOfRoute, segments)),
    [roadIdsOfRoute, segments]
  );
  const firstRoadId = roadIdsOfRoute[0];
  const { t } = useTranslation(['pages', 'forms', 'common', 'modals']);
  // D3 (2026-07-23): navegación a las Orders vinculadas (misma pestaña). La
  // decide la página (`onOpenBasicAction`/`onOpenExternalOrder`) desde el
  // 14-oct: la tabla no usa el router y así se puede pintar en un modal.
  const goToBasicAction = (actionId?: string | null) => {
    if (actionId) onOpenBasicAction?.(actionId);
  };
  const goToExternalOrder = (id?: string | null, type?: string | null) => {
    if (id) onOpenExternalOrder?.(id, type);
  };

  // Mapa→tabla (2026-07-08): clicar un dot de pausa en el mapa selecciona
  // el tram (mismo `onSegmentClick` que las filas), pero en tablas largas
  // (tablet, muchos tramos) la fila puede quedar fuera de viewport — sin
  // esto el usuario no ve qué se seleccionó. `rowRefs` guarda el nodo DOM
  // de cada fila por `segment.id`; el efecto de abajo hace scroll a la fila
  // seleccionada cada vez que cambia (viene de la tabla o del mapa, da
  // igual — si la fila ya está visible, `block: 'nearest'` es un no-op).
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  useEffect(() => {
    if (!selectedSegmentId) return;
    const node = rowRefs.current.get(selectedSegmentId);
    node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedSegmentId]);

  // «Siguiente sin revisar»: la fila al CENTRO, no al borde — con la cabecera
  // de la tabla fija, una fila que queda arriba del todo se esconde debajo.
  useEffect(() => {
    if (!scrollRequest) return;
    const node = rowRefs.current.get(scrollRequest.segmentId);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [scrollRequest]);

  // Orden cronológico real de la patrulla (por qué: el agrupado por
  // sentido + orden por `indexType` hacía que cada sentido reiniciara su
  // propia numeración — "Tramo 1" aparecía una vez por sentido). El número
  // de fila ahora es la posición cronológica 1..N, no `indexType`.
  // El mismo criterio que recorre «Siguiente sin revisar» en la página.
  const sortedSegments = useMemo(
    () => (keepOrder ? segments : sortSegmentsByStartDate(segments)),
    [segments, keepOrder]
  );

  const getDirectionIcon = (
    direction?: string | null
  ): { symbol: string; ascending: boolean | null } | null => {
    const key = directionNameKey(direction);
    if (key === 'pages:surveillance.directionAscending') {
      return { symbol: '↑', ascending: true };
    }
    if (key === 'pages:surveillance.directionDescending') {
      return { symbol: '↓', ascending: false };
    }
    // "Ambos sentidos": símbolo bidireccional ↕ con color neutro (no es
    // ascendente ni descendente) — coherente con el `(↕)` de las tablas de
    // listado (`formatDirectionCellLabel`, feedback usuario 2026-07-21).
    // "Sin especificar" sigue sin símbolo (solo texto), igual que esas tablas.
    if (direction === 'both') {
      return { symbol: '↕', ascending: null };
    }
    return null;
  };

  // Prioriza `directionName` guardado en el segmento (mismo criterio que el
  // acordeón viejo); si no hay, cae al mapeo canónico sentido→key i18n.
  const getDirectionLabel = (segment: RouteSegment): string => {
    if (segment.directionName) return segment.directionName;
    return t(
      directionNameKey(segment.direction) ??
        'pages:surveillance.directionUnspecified'
    );
  };

  // La columna «Cambiar causa» y las acciones de vínculo (29-oct: ya no hay
  // columna «Vincular», viven en «OT sala» y «Act. básica») solo salen si la página pasa
  // sus callbacks, además del permiso: la vista previa de «Registrar parada»
  // pinta esta tabla dentro de un modal y sin ellos (sus piezas aún no existen
  // en el servidor), y un botón sin callback no haría nada.
  const showChangeCauseColumn = canChangeCause && Boolean(onChangeCause);
  const canLinkAvisoHere =
    canLinkAviso && Boolean(onLinkAviso && onChangeAviso && onUnlinkAviso);
  const canLinkActionHere = canLinkAction && Boolean(onLinkAction);

  /**
   * ¿Le falta la básica a este tramo de «Actuación»? Como la card: una básica
   * ANULADA no resuelve el tramo (`isLiveLinkedBasicAction`, 2026-10-08) y
   * manda sobre el aviso del back, que puede no estar al día; sin básica, el
   * aviso del back (`pendingActionLink`) manda si viene.
   */
  const lacksBasicAction = (segment: RouteSegment): boolean =>
    segment.linkedBasicActionOrder
      ? !isLiveLinkedBasicAction(segment.linkedBasicActionOrder)
      : (segment.pendingActionLink ?? true);

  /**
   * ¿Puede este tramo ligar algo ahora? Un tramo pendiente de sincronizar no
   * ofrece nada: su id aún no existe en el servidor. Uno ANULADO tampoco
   * (revisión M1): vincularle una básica o un aviso no resuelve nada.
   */
  const canTakeLinksNow = (segment: RouteSegment): boolean =>
    !isSegmentPendingSync(segment) && canSegmentTakeLinks(segment);
  const canLinkBasicActionHere = (segment: RouteSegment): boolean =>
    segment.pauseCause === 'action' &&
    canLinkActionHere &&
    lacksBasicAction(segment) &&
    canTakeLinksNow(segment);

  // Columna «Revisado»: como las demás, solo con permiso Y callback.
  const showReviewColumn = canReview && Boolean(onToggleReview);
  const reviewProgress = computeReviewProgress(
    sortedSegments,
    reviewOverlay,
    autoReviews
  );
  // «Marcar todos» no manda los que ya van revisados solos. Si todos son
  // automáticos no queda nada que marcar a mano: la cabecera se bloquea.
  const reviewableIds = sortedSegments
    .filter(
      (segment) =>
        isSegmentReviewable(segment) && !autoReviews?.has(segment.id)
    )
    .map((segment) => segment.id);
  const allReviewed =
    reviewProgress.total > 0 && reviewProgress.done === reviewProgress.total;
  const handleHeaderReview = () => {
    if (allReviewed) onRequestUnreviewAll?.();
    else onToggleReview?.(reviewableIds, true);
  };

  // Palabra de cabecera de la columna "Sentido" — se usa para no repetirla
  // dentro de la celda ("Sentido ascendente" bajo la columna "Sentido").
  const directionHeaderWord = t('forms:workOrder.direction');

  // Ancho total de la tabla, para la fila de «sin tramos».
  const columnCount =
    BASE_COLUMN_COUNT +
    (canEditSegments ? 1 : 0) +
    (showChangeCauseColumn ? 1 : 0) +
    (showRoadColumn ? 1 : 0) +
    (showReviewColumn ? 1 : 0);

  // El globo del ✎ (30-oct): quién registró la parada y cuándo.
  const stopTip = (split: NonNullable<RouteSegment['stopSplit']>) =>
    t('surveillance.registerStop.marker', {
      name: split.byName,
      date: format(new Date(split.at), 'dd/MM/yyyy HH:mm'),
    });
  // El globo del ↪ (30-oct): de dónde vino siempre; quién y cuándo, solo si se
  // sabe quién (una hora sin quién no dice nada).
  const transferTip = (transfer: NonNullable<RouteSegment['transfer']>) =>
    transfer.byName
      ? t('surveillance.transferSegment.marker', {
          route: transfer.fromRouteRoid,
          roid: transfer.fromRoid,
          name: transfer.byName,
          date: format(new Date(transfer.at), 'dd/MM/yyyy HH:mm'),
        })
      : t('surveillance.transferSegment.markerNoUser', {
          route: transfer.fromRouteRoid,
          roid: transfer.fromRoid,
        });

  // Nº de cada fila: consecutivo, sin contar las paradas de «Así quedará»
  // (una parada no es un tramo y no gasta número) ni las de Nº fijo (el padre
  // del árbol, que es el tramo de hoy).
  const rowNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    let next = firstNumber;
    for (const row of sortedSegments) {
      const note = rowNotes?.[row.id];
      if (note?.kind === 'stop' || note?.number != null) continue;
      numbers[row.id] = next;
      next += 1;
    }
    return numbers;
  }, [sortedSegments, rowNotes, firstNumber]);

  return (
    <SegmentsContainer>
      <TableScroll>
        <Table>
          <Thead>
            <tr>
              {showReviewColumn && (
                <ReviewTh>
                  <ReviewHeader title={t('surveillance.segmentReview.column')}>
                    <ReviewCheckBox
                      indeterminate={
                        reviewProgress.done > 0 && !allReviewed
                      }
                      checked={allReviewed}
                      disabled={reviewableIds.length === 0}
                      aria-checked={
                        reviewProgress.done > 0 && !allReviewed
                          ? 'mixed'
                          : allReviewed
                      }
                      onChange={handleHeaderReview}
                      aria-label={t('surveillance.segmentReview.column')}
                    />
                    {/* Solo la casilla, para que la columna sea estrecha
                        (2-nov); para qué sirve lo explica la «i» junto a
                        «Tramos». El nombre queda para el lector de pantalla. */}
                    <VisuallyHidden>
                      {t('surveillance.segmentReview.column')}
                    </VisuallyHidden>
                  </ReviewHeader>
                </ReviewTh>
              )}
              {/* UX 2026-07-07 (2ª ronda feedback): la card ya se titula
                  "Tramos" — repetir "Tramo" como cabecera de la columna de
                  numeración era redundante. Símbolo de unidad literal (como
                  "Km"), sin key i18n. Ver flag en la respuesta de la tarea. */}
              <Th>Nº</Th>
              {showRoadColumn && <Th>{t('surveillance.road')}</Th>}
              <Th>{directionHeaderWord}</Th>
              <Th>{t('common:tables.headers.pkInit')}</Th>
              <Th>{t('common:tables.headers.pkEnd')}</Th>
              {/* UX 2026-07-07: unidad física, no copy — literal sin traducir,
                  igual que el propio formato PK "0+000" en las 2 columnas
                  anteriores. Ver flag en la respuesta de la tarea. */}
              <Th>Km</Th>
              <Th>{t('surveillance.startTime')}</Th>
              <Th>{t('surveillance.endTime')}</Th>
              {/* Reusa la key existente forms:timeManagement.headers.duration
                  ("Duración") — ya vive en el namespace `forms` que este
                  componente ya importa, sin key nueva. */}
              <Th>{t('forms:timeManagement.headers.duration')}</Th>
              <Th>{t('surveillance.pause.cause')}</Th>
              {/* D2: detalle de la causa. D3: links a la Act. básica / OT sala
                  vinculadas (headers con claves propias — NO reusar las labels
                  de causa: colisionan con la leyenda del mapa y el dropdown de
                  causa del modal Finalizar). */}
              <Th>{t('common:tables.headers.description')}</Th>
              {/* 30-oct: también lleva la actuación de la OT del aviso. */}
              <Th>{t('surveillance.actionColumn')}</Th>
              <Th>{t('surveillance.linkedExternalColumn')}</Th>
              {/* Corrección de gestión: la columna solo existe si el rol y el
                  estado del recorrido lo permiten (spec 2026-07-31). */}
              {canEditSegments && (
                <Th>{t('surveillance.editSegment.columnHeader')}</Th>
              )}
              {showChangeCauseColumn && (
                <Th>{t('surveillance.changeCause.columnHeader')}</Th>
              )}
            </tr>
          </Thead>
          <Tbody>
            {sortedSegments.length === 0 ? (
              <tr>
                <Td colSpan={columnCount}>
                  <EmptyCellText>{t('surveillance.noSegments')}</EmptyCellText>
                </Td>
              </tr>
            ) : (
              sortedSegments.map((segment, index) => {
                const directionIcon = getDirectionIcon(segment.direction);
                const isActive = !segment.endDate;
                const note = rowNotes?.[segment.id];
                // 30-oct: la actuación de la OT del aviso va en la celda de la
                // actuación (bajo la OT la fila crecía al doble). Mismas reglas
                // que cuando iba allí: solo con OT, ni anulado ni sin sincronizar.
                const avisoWorks =
                  segment.linkedExternalOrder?.id &&
                  segment.state !== 'annulled' &&
                  !isSegmentPendingSync(segment)
                    ? externalWorksBySegmentId?.[segment.id]
                    : undefined;
                const hasAvisoWorks = (avisoWorks?.length ?? 0) > 0;
                const basicStateVariant = orderStateVariant(
                  liveLinkedBasicActionOrder(segment.linkedBasicActionOrder)?.state
                );

                return (
                  <SegmentRow
                    key={segment.id}
                    ref={(node) => {
                      if (node) rowRefs.current.set(segment.id, node);
                      else rowRefs.current.delete(segment.id);
                    }}
                    $selected={
                      segment.id === selectedSegmentId ||
                      !!selectedSegmentIds?.includes(segment.id)
                    }
                    $pending={isSegmentPendingSync(segment)}
                    $muted={!!note?.muted}
                    $stop={note?.kind === 'stop'}
                    $clickable={Boolean(onSegmentClick)}
                    data-preview-kind={note?.kind}
                    onClick={() => onSegmentClick?.(segment)}
                  >
                    {showReviewColumn && (
                      <ReviewCell
                        segment={segment}
                        reviewable={isSegmentReviewable(segment)}
                        display={getReviewDisplay(segment, reviewOverlay, autoReviews)}
                        autoReview={autoReviews?.get(segment.id)}
                        onToggle={(target, reviewed) =>
                          onToggleReview?.([target.id], reviewed)
                        }
                      />
                    )}
                    <NumberTd
                      id={segmentNumberCellId(segment.id)}
                      $depth={note?.tree?.depth ?? 0}
                    >
                      {note?.tree && <TreeGuides tree={note.tree} />}
                      {note?.kind === 'stop' ? (
                        <StopLabel>
                          <CauseIcon aria-hidden="true">⏸</CauseIcon>
                          {note.label}
                        </StopLabel>
                      ) : (
                        <SegmentNumber>
                          {note?.number ?? rowNumbers[segment.id]}
                        </SegmentNumber>
                      )}
                      {note?.badge && (
                        <PreviewBadge data-testid="segment-row-badge">
                          {note.badge}
                        </PreviewBadge>
                      )}
                      {isSegmentPendingSync(segment) && (
                        <PendingMark
                          title={t('modals:timerOverlay.pendingSyncSegment')}
                          data-testid="segment-row-pending"
                        >
                          ⏳
                        </PendingMark>
                      )}
                      {/* 30-oct (el usuario): «Registrado por gestión el…»
                          con hora y sin quién es lo mismo que nada. Con
                          quién, globo que se abre también al tocarlo; sin
                          quién, el lápiz solo. */}
                      {segment.stopSplit &&
                        (segment.stopSplit.byName ? (
                          <MarkTip
                            type="button"
                            data-tooltip={stopTip(segment.stopSplit)}
                            aria-label={stopTip(segment.stopSplit)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="segment-row-split"
                          >
                            ✎
                          </MarkTip>
                        ) : (
                          <SplitMark data-testid="segment-row-split">
                            ✎
                          </SplitMark>
                        ))}
                      {/* 23-oct (el usuario): en la tabla, el icono ↪ (la
                          pastilla «Transferido» no quedaba bien aquí). La
                          pastilla va en la card de «Órdenes asociadas», que
                          además dice de dónde vino y se lee en tablet. */}
                      {segment.transfer && (
                        <MarkTip
                          type="button"
                          data-tooltip={transferTip(segment.transfer)}
                          aria-label={transferTip(segment.transfer)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="segment-row-transferred"
                        >
                          ↪
                        </MarkTip>
                      )}
                    </NumberTd>
                    {showRoadColumn && (
                      <Td>
                        {/* Un tramo que pasó por varias vías las lista TODAS,
                            en el orden en que las recorrió. Sin trozos, la suya
                            — y sin carretera propia, la PRIMERA del recorrido,
                            que es la que tenía implícita. */}
                        {(segment.roadStretches &&
                        segment.roadStretches.length > 0
                          ? segment.roadStretches
                              .map((st) => roadNamesById?.[st.roadId] ?? '')
                              .filter(Boolean)
                              .join(' · ')
                          : roadNamesById?.[
                              segment.roadId ?? firstRoadId ?? ''
                            ]) || '-'}
                      </Td>
                    )}
                    <Td>
                      <DirectionCell>
                        {note?.kind !== 'stop' && directionIcon && (
                          <DirectionIcon $ascending={directionIcon.ascending}>
                            {directionIcon.symbol}
                          </DirectionIcon>
                        )}
                        {/* Una parada de «Así quedará» no tiene sentido. */}
                        {note?.kind === 'stop'
                          ? '-'
                          : stripLeadingWord(
                              getDirectionLabel(segment),
                              directionHeaderWord
                            )}
                      </DirectionCell>
                    </Td>
                    <Td>{formatPK(segment.pkInitKm, segment.pkInitMeter)}</Td>
                    <Td>
                      {segment.pkEndKm != null && segment.pkEndMeter != null
                        ? formatPK(segment.pkEndKm, segment.pkEndMeter)
                        : '-'}
                    </Td>
                    <Td>
                      {/* Una parada no recorre nada (30-oct: «0.0» era ruido). */}
                      {note?.kind === 'stop'
                        ? '-'
                        : formatSegmentKm(
                            segment.pkInitKm,
                            segment.pkInitMeter,
                            segment.pkEndKm,
                            segment.pkEndMeter,
                            segment.roadStretches,
                            {
                              roadId: segment.roadId ?? firstRoadId,
                              meshesByRoadId: meshesDeLasVias,
                            }
                          )}
                    </Td>
                    <Td>{format(new Date(segment.startDate), 'HH:mm')}</Td>
                    <Td>
                      {segment.endDate
                        ? format(new Date(segment.endDate), 'HH:mm')
                        : '-'}
                    </Td>
                    <Td>
                      {formatSegmentDuration(
                        segment.startDate,
                        segment.endDate
                      )}
                    </Td>
                    <Td>
                      {/* La parada de «Así quedará» no repite la causa: la
                          lleva la pieza que cierra (30-oct). Solo explica lo
                          que hace la de un borde. */}
                      {note?.kind === 'stop' ? (
                        note.causeNote ? (
                          <StopNote>{note.causeNote}</StopNote>
                        ) : (
                          '-'
                        )
                      ) : isActive ? (
                        <ActiveBadge>
                          {t('surveillance.segmentState.active')}
                        </ActiveBadge>
                      ) : missingCauseIds?.includes(segment.id) ? (
                        /* «Así quedará» de «Registrar parada» (29-oct): el
                           tramo cuya parada aún no tiene causa. */
                        <PendingCellText>
                          {t('surveillance.registerStop.causeMissing')}
                        </PendingCellText>
                      ) : segment.pauseCause ? (
                        <CauseBadge>
                          <CauseIcon>⏸</CauseIcon>
                          <CauseLabel>
                            {t(
                              `surveillance.pause.causes.${segment.pauseCause}`,
                              {
                                defaultValue: segment.pauseCause,
                              }
                            )}
                          </CauseLabel>
                        </CauseBadge>
                      ) : (
                        '-'
                      )}
                    </Td>
                    {/* D2: Descripción (detalle de la causa). */}
                    <Td>
                      {segment.pauseCauseDetail ? (
                        <DescriptionCellText title={segment.pauseCauseDetail}>
                          {segment.pauseCauseDetail}
                        </DescriptionCellText>
                      ) : (
                        '-'
                      )}
                    </Td>
                    {/* D3: link a la Actuació básica vinculada (misma pestaña).
                        stopPropagation para no disparar el onSegmentClick de la fila. */}
                    <Td onClick={(e) => e.stopPropagation()}>
                    <CellStack>
                        {/* Una básica ANULADA no resuelve el tramo: se pinta
                            como pendiente, no como «Resuelto por» (2026-10-08). */}
                        {liveLinkedBasicActionOrder(segment.linkedBasicActionOrder)
                          ?.actionId ? (
                          <LinkWithState>
                            {onOpenBasicAction ? (
                              <LinkText
                                type="button"
                                onClick={() =>
                                  goToBasicAction(
                                    segment.linkedBasicActionOrder?.actionId
                                  )
                                }
                              >
                                {segment.linkedBasicActionOrder?.roid ??
                                  t('surveillance.linkedBasicActionColumn')}
                              </LinkText>
                            ) : (
                              segment.linkedBasicActionOrder?.roid ??
                              t('surveillance.linkedBasicActionColumn')
                            )}
                            {basicStateVariant && (
                              <SpecialTag
                                variant={basicStateVariant}
                                sizeTag="medium"
                                data-testid="segment-row-basic-state"
                              />
                            )}
                          </LinkWithState>
                        ) : isSegmentPendingAction(segment) &&
                          !canLinkBasicActionHere(segment) ? (
                          /* P15 Fase 2 (2026-08-03): un tramo cerrado pidiendo
                             Actuació que todavía no la tiene se veía EXACTAMENTE
                             igual que uno que nunca la pidió — los dos con «-» —
                             así que desde la pantalla principal del RV era
                             imposible saber que faltaba algo. Lo dice con el
                             mismo tratamiento que su hermano «Pendiente de
                             aviso a sala». */
                          <PendingCellText>
                            {t('surveillance.pendingActionLink')}
                          </PendingCellText>
                        ) : (
                          !canLinkBasicActionHere(segment) &&
                          !hasAvisoWorks &&
                          '-'
                        )}
                        <LinkedWorkTags
                          works={avisoWorks}
                          onOpen={
                            onOpenLinkedWork
                              ? (work) => onOpenLinkedWork(work, segment)
                              : undefined
                          }
                          disabled={busyAvisoSegmentId === segment.id}
                          sizeTag="medium"
                          labelKey="pages:surveillance.fromOtWork"
                          showState
                        />
                        {/* 29-oct: la acción de la antigua columna «Vincular».
                            Quien puede crear la básica ve solo esta acción, que
                            ya dice lo que falta; el ámbar queda para el resto. */}
                        {canLinkBasicActionHere(segment) && (
                            <LinkText
                              type="button"
                              data-testid="segment-row-link-action"
                              onClick={() => onLinkAction?.(segment)}
                            >
                              {t('surveillance.linkToAction')}
                            </LinkText>
                          )}
                    </CellStack>
                    </Td>
                    {/* D3: la OT del aviso (29-oct: pill como en la card, con
                        el menú que antes vivía en la columna «Vincular»). */}
                    <Td onClick={(e) => e.stopPropagation()}>
                      <AvisoOrderCell
                        segment={segment}
                        canLink={canLinkAvisoHere && canTakeLinksNow(segment)}
                        busy={busyAvisoSegmentId === segment.id}
                        onOpenOrder={
                          onOpenExternalOrder
                            ? () =>
                                goToExternalOrder(
                                  segment.linkedExternalOrder?.id,
                                  segment.linkedExternalOrder?.type
                                )
                            : undefined
                        }
                        onLink={() => onLinkAviso?.(segment)}
                        onEdit={() => onChangeAviso?.(segment)}
                        onUnlink={() => onUnlinkAviso?.(segment)}
                      />
                    </Td>
                    {/* Corregir tramo (spec 2026-07-31): stopPropagation para
                        no disparar el onSegmentClick de la fila, que mueve la
                        selección del mapa.
                        Un tramo PENDIENTE (creado sin cobertura, id sintético)
                        no se corrige: todavía no existe en el servidor, así que
                        la mutación viajaría con un id que el backend no conoce.
                        La columna sigue estando —solo se vacía esta celda—;
                        cambiar el colSpan por fila descuadraría la tabla. */}
                    {canEditSegments && (
                      <Td onClick={(e) => e.stopPropagation()}>
                        {/* Un tramo ANULADO tampoco: ni «Corregir», ni
                            «Parada», ni «Transferir» (remate del QA 15-oct). */}
                        {isSegmentPendingSync(segment) ||
                        segment.state === 'annulled' ? (
                          /* La celda se pinta SIEMPRE, con el guion de las
                             demás columnas cuando no hay nada que ofrecer. Si
                             se omite entera, la fila se queda corta y la tabla
                             enseña un hueco donde debería haber una celda
                             vacía (feedback usuario 2026-08-15). */
                          '-'
                        ) : (
                          <CellActions>
                            <LinkText
                              type="button"
                              onClick={() => onEditSegment?.(segment)}
                            >
                              {t('surveillance.editSegment.action')}
                            </LinkText>
                            {/* «Registrar parada» (2026-10-06): solo tramos
                                cerrados — el activo es del cronómetro. Un
                                anulado ya no llega aquí: el servidor lo
                                rechaza y se rellenaba todo para nada (QA
                                15-oct, T1.x+f). */}
                            {canRegisterStops && segment.endDate && (
                              <LinkText
                                type="button"
                                onClick={() => onRegisterStop?.(segment)}
                                data-testid="segment-register-stop"
                              >
                                {t('surveillance.registerStop.link')}
                              </LinkText>
                            )}
                            {canRegisterStops &&
                              segment.stopSplit?.undoable && (
                                <LinkText
                                  type="button"
                                  onClick={() => onUndoStop?.(segment)}
                                  data-testid="segment-undo-stop"
                                >
                                  {t('surveillance.registerStop.undoLink')}
                                </LinkText>
                              )}
                            {/* «Transferir tramo» (2026-10-07): una pieza de
                                una parada registrada no viaja (deshacer antes). */}
                            {canTransferSegments &&
                              segment.endDate &&
                              segment.state !== 'annulled' &&
                              !segment.stopSplit && (
                                <LinkText
                                  type="button"
                                  onClick={() => onTransferSegment?.(segment)}
                                  data-testid="segment-transfer"
                                >
                                  {t('surveillance.transferSegment.link')}
                                </LinkText>
                              )}
                          </CellActions>
                        )}
                      </Td>
                    )}
                    {/* Cambiar la causa (2026-10-07): mismo permiso y mismo
                        modal que la etiqueta de «Órdenes asociadas». Guion en
                        el tramo abierto (su causa se elige al cerrarlo) y en
                        el pendiente de sincronizar (id que el backend aún no
                        conoce). La celda va SIEMPRE, como la de «Corregir». */}
                    {showChangeCauseColumn && (
                      <Td onClick={(e) => e.stopPropagation()}>
                        {isSegmentPendingSync(segment) ||
                        !isSegmentCauseChangeable(segment) ? (
                          '-'
                        ) : (
                          <LinkText
                            type="button"
                            onClick={() => onChangeCause?.(segment)}
                          >
                            {t('surveillance.changeCause.action')}
                          </LinkText>
                        )}
                      </Td>
                    )}
                  </SegmentRow>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableScroll>
    </SegmentsContainer>
  );
}
